import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './index.css';
import { injectTheme, removeTheme, type ThemeMode } from './lib/injectTheme';
import { Dashboard } from './pages/Dashboard';
import { EpisodeListing } from './pages/EpisodeListing';
import { EpisodeDetail } from './pages/EpisodeDetail';
import { ApiTesting } from './pages/ApiTesting';
import { PodcastSettings } from './pages/PodcastSettings';
import { DevToastTester } from './components/DevToastTester';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { UserCompaniesProvider } from './contexts/UserCompaniesContext';
import { RequireRole } from './components/RequireRole';
import { ErrorBoundary } from './components/ErrorBoundary';
import { FeedbackProvider, FeedbackForm } from './components/feedback-widget';
import { HelpProvider, HelpFAB } from './components/help-widget';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import { AppLayout } from './components/AppLayout';
import { api } from '@/services/api';

// Inject --pm-* CSS tokens before first render.
injectTheme();

interface UserProfile {
  id: string;
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
  companyId?: string;
}

function AppContent() {
  const location = useLocation();
  const [user, setUser] = useState<UserProfile | null>(null);

  // Fetch user profile on mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await api.get<UserProfile>('/users/me');
        if (response.success && response.data) {
          setUser(response.data);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUser();
  }, []);

  // Notify shell app of user activity to prevent inactivity timeout
  // The shell app listens for 'child-app-activity' to reset its inactivity timer
  useEffect(() => {
    let lastDispatch = 0;
    const THROTTLE_MS = 5000; // Only dispatch every 5 seconds max

    const notifyActivity = () => {
      const now = Date.now();
      if (now - lastDispatch > THROTTLE_MS) {
        lastDispatch = now;
        window.dispatchEvent(new CustomEvent('child-app-activity'));
      }
    };

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      window.addEventListener(event, notifyActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, notifyActivity);
      });
    };
  }, []);

  // Context collector for the feedback widget
  const contextCollector = React.useCallback(async () => {
    return {
      user: user ? {
        email: user.email,
        displayName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
      } : { email: 'unknown', displayName: 'Unknown User' },
      route: location.pathname,
      companyId: user?.companyId,
      appName: 'Podcast Manager',
    };
  }, [user, location.pathname]);

  return (
    <HelpProvider defaultToolId="episode-composer">
      <FeedbackProvider
        config={{
          apiEndpoint: 'https://feedback-api-production-d285.up.railway.app/api/feedback',
          projectKey: 'MO',
          issueType: 'Bug',
          contextCollector,
          enableScreenshot: true,
          enableErrorTracking: true,
          maxRecentErrors: 5,
        }}
      >
        <BreadcrumbProvider>
        <div className="min-h-screen text-foreground">
          <Routes>
            <Route element={<AppLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="episodes" element={<EpisodeListing />} />
              <Route path="episodes/:id" element={<EpisodeDetail />} />
              <Route
                path="settings"
                element={
                  <RequireRole requiredRole="admin">
                    <PodcastSettings />
                  </RequireRole>
                }
              />
              <Route
                path="api-testing"
                element={
                  <RequireRole requiredRole="super_admin">
                    <ApiTesting />
                  </RequireRole>
                }
              />
              <Route path="*" element={<Navigate to="." replace />} />
            </Route>
          </Routes>
          <DevToastTester />
          <HelpFAB />
          <FeedbackForm />
        </div>
        </BreadcrumbProvider>
      </FeedbackProvider>
    </HelpProvider>
  );
}

/**
 * Props passed from shell via Module Federation.
 * Must match SharedAppData interface in shell's ModuleFederationLoader.
 */
interface AppProps {
  userCompanies?: Array<{ id: string; name: string; color?: string }>;
  themeMode?: ThemeMode;
}

function App({ userCompanies, themeMode }: AppProps) {
  // Apply theme from shell prop
  useEffect(() => {
    if (themeMode) injectTheme(themeMode);
  }, [themeMode]);

  // Listen for live theme toggle events from the shell
  // On unmount, remove the injected <style> tag so stale :root vars
  // don't override the shell's class-based theme after navigating away
  useEffect(() => {
    const handler = (e: Event) => {
      const mode = (e as CustomEvent<ThemeMode>).detail;
      if (mode === 'dark' || mode === 'light') injectTheme(mode);
    };
    window.addEventListener('theme-mode-changed', handler);
    return () => {
      window.removeEventListener('theme-mode-changed', handler);
      removeTheme();
    };
  }, []);

  return (
    <ErrorBoundary>
      <UserCompaniesProvider initialCompanies={userCompanies}>
        <WebSocketProvider>
          <AppContent />
        </WebSocketProvider>
      </UserCompaniesProvider>
    </ErrorBoundary>
  );
}

export default App;
